import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, InputNumber, Row, Space, Statistic, Switch, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getBandwidth, getConnections } from '../services/bandwidth';
import type { BandwidthIpRow, BandwidthResponse, BandwidthUrlRow, ConnectionSnapshot } from '../services/bandwidth';

function fmtBytes(n: number) {
  if (!Number.isFinite(n) || n <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function getErrorMessage(e: unknown) {
  if (typeof e === 'string') return e;
  if (e instanceof Error) return e.message || '获取失败';
  if (e && typeof e === 'object') {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return '获取失败';
}

const Bandwidth: React.FC = () => {
  const [minutes, setMinutes] = useState(10);
  const [tailMB, setTailMB] = useState(20);

  const [bw, setBw] = useState<BandwidthResponse | null>(null);
  const [bwLoading, setBwLoading] = useState(false);
  const [bwError, setBwError] = useState<string | null>(null);

  const [conn, setConn] = useState<ConnectionSnapshot | null>(null);
  const [connError, setConnError] = useState<string | null>(null);

  const [autoRefresh, setAutoRefresh] = useState(false);
  const timerRef = useRef<number | null>(null);

  const refreshBw = async () => {
    setBwLoading(true);
    try {
      const res = await getBandwidth({ minutes, limit: 100, tailBytes: tailMB * 1024 * 1024 });
      setBw(res);
      setBwError(res.error ?? null);
    } catch (e: unknown) {
      setBwError(getErrorMessage(e));
    } finally {
      setBwLoading(false);
    }
  };

  const refreshConn = async () => {
    try {
      const res = await getConnections();
      setConn(res);
      setConnError(null);
    } catch (e: unknown) {
      setConnError(getErrorMessage(e));
    }
  };

  const refreshAll = () => {
    void refreshBw();
    void refreshConn();
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoRefresh) {
      timerRef.current = window.setInterval(refreshAll, 30_000);
    }
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, minutes, tailMB]);

  const urlCols: ColumnsType<BandwidthUrlRow> = useMemo(
    () => [
      { title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true },
      {
        title: '总字节',
        dataIndex: 'totalBytes',
        key: 'totalBytes',
        width: 130,
        sorter: (a, b) => a.totalBytes - b.totalBytes,
        defaultSortOrder: 'descend',
        render: (v: number) => <Tag color={v > 10 * 1024 * 1024 ? 'red' : v > 1024 * 1024 ? 'orange' : 'blue'}>{fmtBytes(v)}</Tag>,
      },
      { title: '次数', dataIndex: 'count', key: 'count', width: 90, sorter: (a, b) => a.count - b.count },
      { title: '平均', dataIndex: 'avgBytes', key: 'avgBytes', width: 110, render: (v: number) => fmtBytes(v) },
      { title: '最大', dataIndex: 'maxBytes', key: 'maxBytes', width: 110, render: (v: number) => fmtBytes(v) },
    ],
    [],
  );

  const ipCols: ColumnsType<BandwidthIpRow> = useMemo(
    () => [
      { title: 'IP', dataIndex: 'ip', key: 'ip' },
      {
        title: '总字节',
        dataIndex: 'totalBytes',
        key: 'totalBytes',
        width: 140,
        sorter: (a, b) => a.totalBytes - b.totalBytes,
        defaultSortOrder: 'descend',
        render: (v: number) => fmtBytes(v),
      },
      { title: '次数', dataIndex: 'count', key: 'count', width: 100, sorter: (a, b) => a.count - b.count },
    ],
    [],
  );

  const peerCols: ColumnsType<{ ip: string; count: number }> = useMemo(
    () => [
      { title: 'IP', dataIndex: 'ip', key: 'ip' },
      { title: '连接数', dataIndex: 'count', key: 'count', width: 120, sorter: (a, b) => a.count - b.count, defaultSortOrder: 'descend' },
    ],
    [],
  );

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 16 }}>带宽分析</h2>

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="说明"
        description={
          <div>
            <div>数据来源于 Nginx access 日志(combined 格式),按时间窗口统计出向字节。URL 已做 /:id 归一化。</div>
            <Typography.Text type="secondary">连接数快照来自服务器 ss -s 命令。默认仅扫描日志尾部 20MB,时间跨度取决于流量密度。</Typography.Text>
          </div>
        }
      />

      <Card
        title="筛选"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <span>时间窗口(分钟)</span>
            <InputNumber min={1} max={1440} value={minutes} onChange={(v) => setMinutes(Number(v || 10))} />
            <span>尾部扫描(MB)</span>
            <InputNumber min={1} max={200} value={tailMB} onChange={(v) => setTailMB(Number(v || 20))} />
            <span>自动刷新(30s)</span>
            <Switch checked={autoRefresh} onChange={setAutoRefresh} />
            <Button type="primary" onClick={refreshAll} loading={bwLoading}>刷新</Button>
          </Space>
        }
      >
        <Row gutter={16}>
          <Col xs={12} sm={6}>
            <Statistic title="窗口总字节" value={fmtBytes(bw?.summary.totalBytes ?? 0)} />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic title="窗口请求数" value={bw?.summary.totalRequests ?? 0} />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic title="平均 QPS" value={bw?.summary.avgQps ?? 0} precision={2} />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic title="实际覆盖(秒)" value={bw?.summary.windowSeconds ?? 0} />
          </Col>
        </Row>
      </Card>

      <Card title="当前连接状态" style={{ marginBottom: 16 }}>
        {connError ? <Alert type="error" showIcon message="获取失败" description={connError} style={{ marginBottom: 12 }} /> : null}
        <Row gutter={16}>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="TCP 总连接" value={conn?.tcpTotal ?? 0} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="ESTABLISHED" value={conn?.estab ?? 0} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="TIME_WAIT" value={conn?.timewait ?? 0} valueStyle={{ color: (conn?.timewait ?? 0) > 3000 ? '#cf1322' : undefined }} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="closed" value={conn?.closed ?? 0} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic title={`到 :${conn?.upstreamPort ?? 3001}`} value={conn?.toUpstream ?? 0} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="orphaned" value={conn?.orphaned ?? 0} />
          </Col>
        </Row>
        <div style={{ marginTop: 12 }}>
          <Typography.Text type="secondary">外网 Top 连接 IP(当前已建立连接,仅供观察):</Typography.Text>
          <Table
            rowKey="ip"
            columns={peerCols}
            dataSource={conn?.topPeers ?? []}
            pagination={false}
            size="small"
            style={{ marginTop: 8 }}
          />
        </div>
      </Card>

      {bwError ? <Alert type="error" showIcon message="带宽分析失败" description={bwError} style={{ marginBottom: 12 }} /> : null}

      <Card title="Top URL —— 按总字节排序(最吃带宽的接口)" style={{ marginBottom: 16 }}>
        <Table
          rowKey="url"
          columns={urlCols}
          dataSource={bw?.topByBytes ?? []}
          loading={bwLoading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="small"
          scroll={{ x: 800 }}
        />
      </Card>

      <Card title="Top URL —— 按调用次数排序" style={{ marginBottom: 16 }}>
        <Table
          rowKey="url"
          columns={urlCols}
          dataSource={bw?.topByCount ?? []}
          loading={bwLoading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="small"
          scroll={{ x: 800 }}
        />
      </Card>

      <Card title="Top IP —— 按消耗带宽排序">
        <Table
          rowKey="ip"
          columns={ipCols}
          dataSource={bw?.topIps ?? []}
          loading={bwLoading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          size="small"
          scroll={{ x: 500 }}
        />
      </Card>
    </div>
  );
};

export default Bandwidth;
